import type { MetricName } from "schemas/generated/analyticsRead";
import type { ChartInterval } from "schemas/utils/analytics";

import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { NoChartData } from "@v1/components/designSystem/shadcn/NoChartData";
import { calculateDelta } from "@v1/features/analytics/analytics";
import AnalyticsFilters from "@v1/features/analytics/AnalyticsFilters";
import { getChartIntervalOptions } from "@v1/features/analytics/chart";
import { ConditionallyBlurredComponent } from "@v1/features/analytics/ConditionallyBlurredComponent";
import { DETAILS_PAGE_CARD_METRICS } from "@v1/features/analytics/constants";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { MetricsDocButton } from "@v1/features/analytics/MetricsDocButton";
import MetricWithDelta from "@v1/features/analytics/MetricWithDelta";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import { Loader } from "@v2/components/Loader";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import { formatValueToTableCell } from "@v2/features/analytics/utils/formatMetric";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useNavigate } from "react-router-dom";

import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { ReploLineChart } from "./ReploLineChart";
import useCurrentAnalyticsTimezone from "./useCurrentAnalyticsTimezone";

const PageDetails: React.FC = () => {
  const { workspace, isLoading } = useCurrentWorkspaceContext();

  if (!workspace) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  return <LoadedPageDetails />;
};

const LoadedPageDetails: React.FC = () => {
  const { query, dispatchAnalyticsQuery, urlHostsObj } =
    useAnalyticsQueryContext();
  const timeZone = useCurrentAnalyticsTimezone();
  const currency = useCurrentAnalyticsCurrencyCode();
  const analytics = useLogAnalytics();

  const navigate = useNavigate();
  const { dataDisplayType } = useAnalyticsStatus();

  /**
   * NOTE (Max, 2024-12-15): When landing on the deep dive page,
   * the query should already have the EQUALS urlPath filter.
   * If it doesn't, it means that we clicked the "Deep Dive" tab on the
   * DashboardMenuItems, in which case we just default to "/".
   *
   * If we default to "/", we'll need to update query's urlPath filters,
   * which is what we do in the useEffect() below.
   */
  const pageUrlPath = React.useMemo(() => {
    return (
      query.filters.urlPath.find((filter) => filter.operator === "equals")
        ?.value[0] ?? "/"
    );
  }, [query.filters.urlPath]);

  const { intervalOptions } = getChartIntervalOptions(
    query.selectedTimePeriod,
    timeZone,
  );

  // TODO (Gabe, 2025-08-19): This needs to be cleaned up, we should be using a
  // route to satisfy this

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  React.useEffect(() => {
    dispatchAnalyticsQuery({
      type: "initDeepDivePage",
      payload: {
        urlPath: pageUrlPath,
      },
    });
  }, []);

  const { mainRangeResults, compareAtRangeResults, isLoading } =
    useBasicAnalyticsRead({
      type: "withUrlSyncQuery",
      queryConfig: {
        useChartInterval: false,
      },
    });

  const {
    mainRangeResults: chartMainRangeResults,
    compareAtRangeResults: chartCompareAtRangeResults,
    isLoading: isChartResultsLoading,
    constructedQuery: chartQuery,
  } = useBasicAnalyticsRead({
    type: "withUrlSyncQuery",
    queryConfig: {
      useChartInterval: true,
    },
  });

  const handleIntervalChange = (newInterval: ChartInterval) => {
    dispatchAnalyticsQuery({
      type: "updateChartInterval",
      payload: newInterval,
    });
  };

  const selectedIntervalOption = intervalOptions.find(
    (option) => option.value === query.chartInterval,
  );

  const pageTitle = mainRangeResults[0]?.metadata.title;

  const metricsToPlot: {
    key: MetricName;
    label: string;
  }[] = [
    {
      key: "conversion_rates",
      label: "Conversion Rate",
    },
    {
      key: "unique_sessions",
      label: "Total Sessions",
    },
    {
      key: "average_order_value",
      label: "Average Order Value",
    },
    {
      key: "revenue_per_session",
      label: "Revenue Per Session",
    },
  ];

  return (
    <div className="flex flex-col gap-4 overflow-hidden">
      <PageHeader
        title={pageTitle ?? "Page Details"}
        showDivider={false}
        onBackClick={() => {
          navigate(-1);
        }}
        actions={<MetricsDocButton />}
      />
      <div className="flex flex-col h-full overflow-auto gap-2">
        <AnalyticsFilters />
        {isLoading && urlHostsObj.isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {DETAILS_PAGE_CARD_METRICS.map((metric, index) => {
              const metricData =
                mainRangeResults[0]?.metrics[metric.key as MetricName];
              const compareAtMetricData =
                compareAtRangeResults[0]?.metrics[metric.key as MetricName];
              const delta = calculateDelta(
                metricData?.[0],
                compareAtMetricData?.[0],
              );
              const doesCompareValueExist = (compareAtMetricData?.[0] ?? 0) > 0;

              return (
                <div
                  key={index}
                  className="border-border rounded-xl border p-6"
                >
                  <div className="flex flex-col">
                    <div className="text-default pb-2 text-base font-medium">
                      <div className="inline-block">{metric.label}</div>
                    </div>
                    <MetricWithDelta
                      name={metric.key}
                      value={metricData?.[0] ?? null}
                      delta={delta}
                      doesCompareValueExist={doesCompareValueExist}
                      wrapperClassName="justify-left"
                      valueClassName="text-2xl font-semibold min-w-[40px] text-default"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex w-full flex-row sm:justify-end justify-start">
          <Combobox.Root
            options={intervalOptions}
            onChange={(value) => {
              handleIntervalChange(value as ChartInterval);
              analytics("analytics.chart.update", {
                time: value as ChartInterval,
              });
            }}
            value={query.chartInterval}
          >
            <Combobox.Trigger>
              <Combobox.SelectionButton
                title={selectedIntervalOption?.label ?? "Interval: "}
                isPlaceholder={!selectedIntervalOption}
              />
            </Combobox.Trigger>
            <Combobox.Popover layoutClassName="w-[90px]" align="end">
              <Combobox.Content />
            </Combobox.Popover>
          </Combobox.Root>
        </div>
        {isChartResultsLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metricsToPlot.map((metricToPlot) => {
              const mainRangeMetrics =
                chartMainRangeResults[0]?.metrics[metricToPlot.key] ?? [];
              const compareAtRangeMetrics =
                chartCompareAtRangeResults[0]?.metrics[metricToPlot.key] ?? [];

              const hasNoChartData =
                mainRangeMetrics.every((value) => value === 0) &&
                compareAtRangeMetrics.every((value) => value === 0);

              return (
                <div
                  key={metricToPlot.key}
                  className="border-0.5 border-border flex flex-col space-y-6 rounded-xl p-6"
                >
                  <span className="typ-header-h5">{metricToPlot.label}</span>
                  {hasNoChartData ? (
                    <NoChartData />
                  ) : (
                    <ConditionallyBlurredComponent
                      shouldBlur={dataDisplayType === "partialData"}
                      blurSize="sm"
                      visibleComponent={
                        <AnalyticsEmptyState type="unlockAnalytics" />
                      }
                      componentToBlur={
                        <ReploLineChart
                          primaryPeriod={mainRangeMetrics}
                          secondaryPeriod={compareAtRangeMetrics}
                          ranges={chartQuery.ranges}
                          chartInterval={query.chartInterval}
                          timeZone={timeZone}
                          tooltipValueFormatter={(value) => {
                            return formatValueToTableCell({
                              name: metricToPlot.key,
                              value,
                              currency,
                              isFractional: query.isFractional,
                            });
                          }}
                          className="h-[250px]"
                        />
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageDetails;
